import {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
  ColumnPinningState,
} from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { EmptyTableMessage } from "@/core/components/empty-table-message";
import { SimpleTable } from "@/core/components/simple-table";
import { createSelectionColumn } from "@/core/components/table-columns/selection-column";
import { Stack } from "@/core/components/ui/stack";
import { useBusiness } from "@/core/business";
import { SpiffSidebarSection } from "@/spiffs/constants";
import { useSpiffSidebar } from "@/spiffs/context/spiff-sidebar-context";
import { EnrichedUserWithSpiffs } from "@/user/user";
import {
  ClosedReferralCol,
  NewReferralCol,
  SignupsCol,
  TechnicianWithRequestCol,
  PaymentAmountCol,
  ReviewsCol,
  YardSignsCol,
  PricebookSalesCol,
  TechGeneratedLeadsCol,
  DrivingScoresCol,
  GenericCol,
  DismissedCol,
} from ".";

interface SpiffPaymentsTableProps {
  users: EnrichedUserWithSpiffs[];
  selectedUsers: RowSelectionState;
  setSelectedUsers: OnChangeFn<RowSelectionState>;
  isLoading: boolean;
  sidebarSection: SpiffSidebarSection;
  customColumns?: ColumnDef<EnrichedUserWithSpiffs>[];
  columnPinning?: Partial<ColumnPinningState>;
  onRowClick?: (row: EnrichedUserWithSpiffs) => void;
  hideSelectionColumn?: boolean;
}

export const SpiffPaymentsTable = ({
  users,
  selectedUsers,
  setSelectedUsers,
  isLoading,
  sidebarSection,
  customColumns,
  columnPinning: customColumnPinning,
  onRowClick: customOnRowClick,
  hideSelectionColumn = false,
}: SpiffPaymentsTableProps) => {
  const { isSpiffOnly } = useBusiness();
  const { updateSidebar, state } = useSpiffSidebar();

  const shouldScroll = users.length > 8;

  const columns: ColumnDef<EnrichedUserWithSpiffs>[] = useMemo(() => {
    const baseColumns = [
      !hideSelectionColumn && createSelectionColumn<EnrichedUserWithSpiffs>(),
      TechnicianWithRequestCol<EnrichedUserWithSpiffs>(),
      !isSpiffOnly && SignupsCol<EnrichedUserWithSpiffs>(),
      !isSpiffOnly && NewReferralCol<EnrichedUserWithSpiffs>(),
      !isSpiffOnly && ClosedReferralCol<EnrichedUserWithSpiffs>(),
      ReviewsCol<EnrichedUserWithSpiffs>(),
      YardSignsCol<EnrichedUserWithSpiffs>(),
      PricebookSalesCol<EnrichedUserWithSpiffs>(),
      TechGeneratedLeadsCol<EnrichedUserWithSpiffs>(),
      DrivingScoresCol<EnrichedUserWithSpiffs>(),
      GenericCol<EnrichedUserWithSpiffs>(),
      DismissedCol<EnrichedUserWithSpiffs>(),
      PaymentAmountCol<EnrichedUserWithSpiffs>(),
    ].filter(Boolean) as ColumnDef<EnrichedUserWithSpiffs>[];

    if (customColumns) {
      return [...baseColumns, ...customColumns];
    }

    return baseColumns;
  }, [customColumns, isSpiffOnly, hideSelectionColumn]);

  const columnPinning: ColumnPinningState = useMemo(() => {
    const defaultPinning: ColumnPinningState = {
      left: [
        ...(hideSelectionColumn ? [] : ["select"]),
        "technician-with-request",
      ],
      right: ["payment-amount"],
    };

    if (!customColumnPinning) {
      return defaultPinning;
    }

    return {
      left: [
        ...(defaultPinning.left || []),
        ...(customColumnPinning.left || []),
      ],
      right: [
        ...(defaultPinning.right || []),
        ...(customColumnPinning.right || []),
      ],
    };
  }, [customColumnPinning, hideSelectionColumn]);

  const handleRowClick = useCallback(
    (row: EnrichedUserWithSpiffs) => {
      // Use custom row click handler if provided
      if (customOnRowClick) {
        customOnRowClick(row);
        return;
      }

      // Default row click behavior
      if (state.selectedItemId === row.id) {
        updateSidebar({
          selectedItemId: null,
          isOpen: false,
        });
        return;
      }

      updateSidebar({
        isOpen: true,
        section: sidebarSection,
        selectedItemId: row.id,
      });
    },
    [customOnRowClick, state.selectedItemId, updateSidebar, sidebarSection],
  );

  if (users.length === 0 && !isLoading) {
    return (
      <Stack className="p-8">
        <EmptyTableMessage />
      </Stack>
    );
  }

  return (
    <SimpleTable
      columns={columns}
      data={users}
      enableRowSelection
      rowSelection={selectedUsers}
      onRowSelectionChange={setSelectedUsers}
      getRowId={(row) => row.id}
      onRowClick={handleRowClick}
      selectedRowId={state.selectedItemId}
      scrollAreaClassName="pr-0"
      scrollAreaHeightClassName={shouldScroll ? "h-[600px]" : undefined}
      className="flex min-h-0 flex-1"
      tableClassName="min-w-[1200px]"
      columnPinning={columnPinning}
      isLoading={isLoading}
      increaseHeaderPadding
    />
  );
};
